import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Filter, 
  Car, 
  RotateCcw,
  Check,
  SlidersHorizontal,
  Wrench,
  ClipboardList,
  LayoutDashboard,
  Building
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UniversalReportFilters {
  reportType: string;
  dateRange?: { from?: Date; to?: Date };
  vehicleIds: string[];
  driverIds: string[];
  costCenterIds: string[];
  groupBy: 'day' | 'week' | 'month' | 'vehicle' | 'driver';
  topN: number;
  // Opções do PDF Consolidado Profissional
  consolidatedPdfMode?: 'executive' | 'top-n' | 'complete';
  consolidatedPdfTopN?: number;
  consolidatedPdfMaxVehicles?: number;
  // Filtros de Manutenção
  osStatus?: 'all' | 'open' | 'closed' | 'pending';
  planIds?: string[];
}

interface UniversalFiltersSheetProps {
  filters: UniversalReportFilters;
  onFilterChange: (filters: UniversalReportFilters) => void;
  onApply: () => void;
  onReset: () => void;
  trigger?: React.ReactNode;
  requiresPeriod?: boolean;
}

export function UniversalFiltersSheet({ 
  filters, 
  onFilterChange, 
  onApply, 
  onReset,
  trigger
}: UniversalFiltersSheetProps) {
  const [open, setOpen] = useState(false);
  const { activeCompany } = useAuth();

  // Buscar veículos da empresa
  const { data: vehicles = [] } = useQuery({
    queryKey: ['fleet-vehicles-select', activeCompany?.id],
    queryFn: async () => {
      if (!activeCompany?.id) return [];
      const { data } = await supabase
        .from('fleet_vehicles')
        .select('id, plate, make, model, cost_center_id')
        .eq('company_id', activeCompany.id)
        .eq('status', 'active')
        .order('plate');
      return data || [];
    },
    enabled: !!activeCompany?.id
  });

  // Buscar planos de manutenção (apenas quando report é maintenance)
  const { data: maintenancePlans = [] } = useQuery({
    queryKey: ['maintenance-plans-select', activeCompany?.id],
    queryFn: async () => {
      if (!activeCompany?.id) return [];
      const { data } = await supabase
        .from('fleet_maint_plans')
        .select('id, name')
        .eq('company_id', activeCompany.id)
        .order('name');
      return data || [];
    },
    enabled: !!activeCompany?.id && filters.reportType === 'maintenance'
  });

  // Buscar centros de custo (frotas) com contagem de veículos
  const { data: costCenters = [] } = useQuery({
    queryKey: ['cost-centers-with-vehicle-count', activeCompany?.id],
    queryFn: async () => {
      if (!activeCompany?.id) return [];
      const { data } = await supabase
        .from('cost_centers')
        .select('id, name')
        .eq('company_id', activeCompany.id)
        .eq('status', 'active')
        .order('name');
      
      // Buscar contagem de veículos por centro de custo
      const centersWithCount = await Promise.all(
        (data || []).map(async (cc) => {
          const { count } = await supabase
            .from('fleet_vehicles')
            .select('id', { count: 'exact', head: true })
            .eq('cost_center_id', cc.id)
            .eq('status', 'active');
          return { ...cc, vehicleCount: count || 0 };
        })
      );
      
      return centersWithCount;
    },
    enabled: !!activeCompany?.id && filters.reportType === 'maintenance-detailed'
  });

  // Determinar quais filtros mostrar baseado no reportType
  const showVehicleFilter = ['vehicles', 'maintenance', 'maintenance-detailed', 'fuel'].includes(filters.reportType);
  const showFleetFilter = filters.reportType === 'maintenance-detailed';
  const showMaintenanceFilters = filters.reportType === 'maintenance';
  const showNoFiltersMessage = filters.reportType === 'consolidated';

  const toggleCostCenter = (costCenterId: string) => {
    const currentIds = filters.costCenterIds || [];
    const newIds = currentIds.includes(costCenterId)
      ? currentIds.filter(id => id !== costCenterId)
      : [...currentIds, costCenterId];
    onFilterChange({ ...filters, costCenterIds: newIds });
  };

  const activeFiltersCount = [
    filters.vehicleIds.length > 0,
    (filters.costCenterIds?.length || 0) > 0,
    filters.osStatus && filters.osStatus !== 'all',
    filters.planIds && filters.planIds.length > 0
  ].filter(Boolean).length;

  const toggleVehicle = (vehicleId: string) => {
    const newIds = filters.vehicleIds.includes(vehicleId)
      ? filters.vehicleIds.filter(id => id !== vehicleId)
      : [...filters.vehicleIds, vehicleId];
    onFilterChange({ ...filters, vehicleIds: newIds });
  };

  const togglePlan = (planId: string) => {
    const currentPlanIds = filters.planIds || [];
    const newIds = currentPlanIds.includes(planId)
      ? currentPlanIds.filter(id => id !== planId)
      : [...currentPlanIds, planId];
    onFilterChange({ ...filters, planIds: newIds });
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  // Se o relatório for consolidado, não mostra o sheet
  if (filters.reportType === 'consolidated') {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            Filtros
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-[420px] sm:w-[540px] p-0">
        <SheetHeader className="p-6 pb-4 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2 text-lg">
                <Filter className="h-5 w-5 text-primary" />
                Filtros do Relatório
              </SheetTitle>
              <SheetDescription className="mt-1">
                Configure os dados incluídos no relatório
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="p-6 space-y-6">
            
            {/* Mensagem para relatório consolidado (não deve aparecer pois retornamos null acima) */}
            {showNoFiltersMessage && (
              <div className="text-center py-8 text-muted-foreground">
                <LayoutDashboard className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Relatório Gerencial Completo</p>
                <p className="text-sm">Este relatório inclui todas as informações da frota.</p>
                <p className="text-sm">Nenhum filtro adicional necessário.</p>
              </div>
            )}

            {/* Filtro de Frota (Centro de Custo) */}
            {showFleetFilter && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <Building className="h-4 w-4 text-green-500" />
                    Frota (Centro de Custo)
                  </Label>
                  <Badge variant="outline" className="text-xs">
                    {(filters.costCenterIds?.length || 0) === 0 ? 'Todas' : `${filters.costCenterIds?.length} selecionadas`}
                  </Badge>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {costCenters.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nenhuma frota encontrada
                    </p>
                  ) : (
                    costCenters.map(cc => (
                      <div
                        key={cc.id}
                        className="flex items-center justify-between gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleCostCenter(cc.id)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={(filters.costCenterIds || []).includes(cc.id)}
                            onCheckedChange={() => toggleCostCenter(cc.id)}
                          />
                          <span className="text-sm">{cc.name}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {cc.vehicleCount} veículos
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Filtro de Veículos */}
            {showVehicleFilter && (
              <>
                {showFleetFilter && <Separator />}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Car className="h-4 w-4 text-blue-500" />
                      Veículos
                    </Label>
                    <Badge variant="outline" className="text-xs">
                      {filters.vehicleIds.length === 0 ? 'Todos' : `${filters.vehicleIds.length} selecionados`}
                    </Badge>
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {vehicles.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        Nenhum veículo encontrado
                      </p>
                    ) : (
                      vehicles.map(vehicle => (
                        <div
                          key={vehicle.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => toggleVehicle(vehicle.id)}
                        >
                          <Checkbox
                            checked={filters.vehicleIds.includes(vehicle.id)}
                            onCheckedChange={() => toggleVehicle(vehicle.id)}
                          />
                          <span className="text-sm">
                            {vehicle.plate} - {vehicle.make} {vehicle.model}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Filtros de Manutenção */}
            {showMaintenanceFilters && (
              <>
                <Separator />
                
                {/* Status da OS */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-orange-500" />
                    Status da OS
                  </Label>
                  <Select 
                    value={filters.osStatus || 'all'} 
                    onValueChange={(value: 'all' | 'open' | 'closed' | 'pending') => 
                      onFilterChange({ ...filters, osStatus: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="open">Abertas</SelectItem>
                      <SelectItem value="closed">Fechadas</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Plano de Manutenção */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-purple-500" />
                      Plano de Manutenção
                    </Label>
                    <Badge variant="outline" className="text-xs">
                      {!filters.planIds || filters.planIds.length === 0 ? 'Todos' : `${filters.planIds.length} selecionados`}
                    </Badge>
                  </div>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {maintenancePlans.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        Nenhum plano encontrado
                      </p>
                    ) : (
                      maintenancePlans.map(plan => (
                        <div
                          key={plan.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => togglePlan(plan.id)}
                        >
                          <Checkbox
                            checked={(filters.planIds || []).includes(plan.id)}
                            onCheckedChange={() => togglePlan(plan.id)}
                          />
                          <span className="text-sm">{plan.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Sem filtros disponíveis */}
            {!showVehicleFilter && !showMaintenanceFilters && !showNoFiltersMessage && (
              <div className="text-center py-8 text-muted-foreground">
                <Filter className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Nenhum filtro disponível</p>
                <p className="text-sm">Selecione um tipo de relatório para ver os filtros.</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Action Buttons */}
        <div className="p-6 border-t bg-muted/30 space-y-3">
          <Button 
            className="w-full h-11 font-semibold" 
            onClick={handleApply}
            disabled={!filters.reportType}
          >
            <Check className="h-4 w-4 mr-2" />
            Aplicar Filtros
          </Button>
          <Button 
            variant="ghost" 
            className="w-full text-muted-foreground" 
            onClick={onReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Restaurar Padrões
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
